class FFStarterBundle extends HTMLElement {
  connectedCallback() {
    // Query every slot, not just ones that already have a variant at page load —
    // a slot with no default product can still gain one later via the swap
    // popup, and it must be included in pricing/add-to-cart once that happens.
    this.items = Array.from(this.querySelectorAll('.ff-starter-bundle__layer'));
    this.btn = this.querySelector('[data-bundle-add]');
    this.regularEl = this.querySelector('[data-bundle-regular]');
    this.saveEl = this.querySelector('[data-bundle-save]');
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.discount = parseInt(this.dataset.discount || '0', 10);

    this.updatePricing();
    this.btn?.addEventListener('click', () => this.addBundle());
    this.initStackSlider();
    this.initSwapModals();
  }

  initSwapModals() {
    // <modal-dialog> relocates itself to document.body in its own
    // connectedCallback (assets/global.js), which runs before this element's
    // callback since that script loads earlier — by the time we get here the
    // popups are no longer descendants of <ff-starter-bundle>, so they must
    // be found via the section id each one carries instead of DOM containment.
    const sectionId = this.dataset.sectionId;
    const swapModals = Array.from(document.querySelectorAll(`.ff-bundle-swap-modal[data-bundle-id="${sectionId}"]`));

    swapModals.forEach((modal) => {
      modal.querySelectorAll('[data-ff-bundle-swap-option]').forEach((option) => {
        option.addEventListener('click', () => this.selectSwapOption(option));
      });

      modal.querySelectorAll('[data-ff-bundle-swap-prev], [data-ff-bundle-swap-next]').forEach((arrow) => {
        arrow.addEventListener('click', () => {
          const track = arrow.closest('.ff-bundle-swap-modal__viewport')?.querySelector('[data-ff-bundle-swap-track]');
          if (!track) return;
          const item = track.querySelector('.ff-bundle-swap-modal__item');
          const styles = window.getComputedStyle(track);
          const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
          const step = item ? item.offsetWidth + gap : track.clientWidth * 0.8;
          track.scrollBy({ left: arrow.hasAttribute('data-ff-bundle-swap-prev') ? -step : step, behavior: 'smooth' });
        });
      });
    });
  }

  selectSwapOption(option) {
    const slotIndex = option.dataset.slotIndex;
    const layer = this.querySelector(`.ff-starter-bundle__layer[data-slot-index="${slotIndex}"]`);
    if (!layer) return;

    layer.dataset.variantId = option.dataset.variantId;
    layer.dataset.price = option.dataset.price;
    layer.dataset.available = option.dataset.available;

    const priceEl = layer.querySelector('.ff-starter-bundle__card-price');
    if (priceEl) priceEl.textContent = this.formatMoney(parseInt(option.dataset.price || '0', 10));

    const mediaLink = layer.querySelector('.ff-starter-bundle__media');
    if (mediaLink) {
      mediaLink.removeAttribute('tabindex');
      mediaLink.removeAttribute('aria-disabled');
      mediaLink.setAttribute('href', option.dataset.url || '#');
      let img = mediaLink.querySelector('img');
      if (!img && option.dataset.image) {
        // A slot with no default product renders a placeholder SVG instead of
        // an <img>, so create one the first time it gets a real product.
        mediaLink.innerHTML = '';
        img = document.createElement('img');
        mediaLink.appendChild(img);
      }
      if (img && option.dataset.image) {
        img.src = option.dataset.image;
        img.alt = option.dataset.title || '';
        img.removeAttribute('srcset');
      }
    }

    layer.classList.remove('ff-starter-bundle__layer--placeholder');

    const nameLink = layer.querySelector('.ff-starter-bundle__name a');
    if (nameLink) {
      nameLink.textContent = option.dataset.title;
      nameLink.setAttribute('href', option.dataset.url || '#');
    }

    const summaryItem = this.querySelector(`.ff-starter-bundle__summary-item[data-slot-index="${slotIndex}"]`);
    if (summaryItem) {
      const nameEl = summaryItem.querySelector('[data-bundle-summary-name]');
      const priceEl2 = summaryItem.querySelector('[data-bundle-summary-price]');
      if (nameEl) nameEl.textContent = option.dataset.title;
      if (priceEl2) priceEl2.textContent = this.formatMoney(parseInt(option.dataset.price || '0', 10));
    }

    const modal = option.closest('modal-dialog');
    modal?.querySelectorAll('[data-ff-bundle-swap-option]').forEach((el) => {
      el.classList.toggle('is-selected', el === option);
    });

    this.updatePricing();
    modal?.hide();
  }

  initStackSlider() {
    const track = this.querySelector('[data-ff-bundle-track]');
    const prevBtn = this.querySelector('[data-ff-bundle-prev]');
    const nextBtn = this.querySelector('[data-ff-bundle-next]');

    if (!track || !prevBtn || !nextBtn) return;

    const scrollStep = () => {
      const item = track.querySelector('.ff-starter-bundle__layer');
      if (!item) return track.clientWidth * 0.85;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return item.offsetWidth + gap;
    };

    prevBtn.addEventListener('click', () => {
      track.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
      track.scrollBy({ left: scrollStep(), behavior: 'smooth' });
    });
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    return (cents / 100).toFixed(2);
  }

  updatePricing() {
    let total = 0;
    let count = 0;

    this.items.forEach((item) => {
      const price = parseInt(item.dataset.price || '0', 10);
      const available = item.dataset.available === 'true';
      if (price > 0 && available) {
        total += price;
        count += 1;
      }
    });

    const savings = Math.round((total * this.discount) / 100);
    const bundle = total - savings;

    if (this.regularEl) {
      this.regularEl.textContent = count > 0 ? this.formatMoney(total) : '—';
    }
    if (this.saveEl) {
      this.saveEl.textContent = count > 0 && savings > 0 ? `-${this.formatMoney(savings)}` : '—';
    }
    if (this.totalEl) {
      this.totalEl.textContent = count > 0 ? this.formatMoney(bundle) : '—';
    }
    if (this.btn) {
      this.btn.disabled = count === 0;
    }
  }

  async addBundle() {
    if (!this.btn || this.btn.disabled) return;

    const items = this.items
      .filter((item) => item.dataset.available === 'true' && item.dataset.variantId)
      .map((item) => ({
        id: parseInt(item.dataset.variantId, 10),
        quantity: 1,
      }));

    if (items.length === 0) return;

    this.btn.classList.add('loading');
    this.btn.disabled = true;

    const cartDrawer = document.querySelector('cart-drawer');

    try {
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error('Add failed');

      const data = await response.json();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ff-starter-bundle', cartData: data });
      }

      if (cartDrawer?.open) {
        // The drawer's root element carries a server-rendered "is-empty" class when the
        // page first loaded with an empty cart; the async section refresh above only
        // swaps inner content, so that stale class must be cleared explicitly or the
        // drawer keeps showing its empty state until the next full page load.
        cartDrawer.classList.remove('is-empty');
        document.querySelector('cart-drawer-items')?.classList.remove('is-empty');
        document.querySelector('.drawer__footer')?.classList.remove('is-empty');
        // onCartUpdate() only swaps cart-drawer-items/footer/heading — it never removes
        // this leftover empty-state block from the initial (empty-cart) page render, which
        // otherwise still fills the drawer and squashes the real items out of view.
        document.querySelector('.drawer__inner-empty')?.remove();
        cartDrawer.open();
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.btn.classList.remove('loading');
      this.btn.disabled = false;
      this.updatePricing();
    }
  }
}

customElements.define('ff-starter-bundle', FFStarterBundle);
